import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminAboutPage = () => {
  const { getAuthHeader } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [paragraphs, setParagraphs] = useState(['', '', '']);
  const [values, setValues] = useState([
    { icon: '', title: '', description: '' },
    { icon: '', title: '', description: '' },
    { icon: '', title: '', description: '' },
    { icon: '', title: '', description: '' }
  ]);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const response = await axios.get(`${API}/about`);
      if (response.data.story_paragraphs?.length > 0) {
        setParagraphs(response.data.story_paragraphs);
      }
      if (response.data.values?.length > 0) {
        setValues(response.data.values);
      }
    } catch (error) {
      toast.error('Failed to load about content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        story_paragraphs: paragraphs.filter(p => p.trim() !== ''),
        values: values.filter(v => v.title.trim() !== '')
      };

      await axios.put(`${API}/about`, data, { headers: getAuthHeader() });
      toast.success('About page updated successfully');
    } catch (error) {
      toast.error('Failed to save');
      console.error(error);
    }
  };

  const addParagraph = () => {
    setParagraphs([...paragraphs, '']);
  };

  const removeParagraph = (index) => {
    setParagraphs(paragraphs.filter((_, i) => i !== index));
  };

  const updateParagraph = (index, value) => {
    const newParagraphs = [...paragraphs];
    newParagraphs[index] = value;
    setParagraphs(newParagraphs);
  };

  const addValue = () => {
    setValues([...values, { icon: '', title: '', description: '' }]);
  };

  const removeValue = (index) => {
    setValues(values.filter((_, i) => i !== index));
  };

  const updateValue = (index, field, value) => {
    const newValues = [...values];
    newValues[index][field] = value;
    setValues(newValues);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-about-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="icon" className="rounded-full">
                  <ArrowLeft size={18} />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900">Edit About Page</h1>
                <p className="text-slate-600">Manage your company story and values</p>
              </div>
            </div>
            <Button onClick={handleSave} className="bg-[#10B981] hover:bg-[#059669] rounded-full" data-testid="save-about-button">
              <Save size={18} className="mr-2" />
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Story Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900">Our Story</h2>
            <Button onClick={addParagraph} variant="outline" className="rounded-full" size="sm">
              <Plus size={16} className="mr-2" />
              Add Paragraph
            </Button>
          </div>
          
          <div className="space-y-6">
            {paragraphs.map((paragraph, index) => (
              <div key={index} className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <Label className="mb-2 block">Paragraph {index + 1}</Label>
                    <Textarea
                      value={paragraph}
                      onChange={(e) => updateParagraph(index, e.target.value)}
                      rows={4}
                      placeholder="Enter paragraph text..."
                      data-testid={`story-paragraph-${index}`}
                    />
                  </div>
                  {paragraphs.length > 1 && (
                    <Button
                      onClick={() => removeParagraph(index)}
                      variant="outline"
                      size="icon"
                      className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 mt-8"
                    >
                      <Trash2 size={18} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Values Section */}
        <div className="bg-white rounded-2xl p-8 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900">Our Values</h2>
            <Button onClick={addValue} variant="outline" className="rounded-full" size="sm">
              <Plus size={16} className="mr-2" />
              Add Value
            </Button>
          </div>

          <div className="space-y-8">
            {values.map((value, index) => (
              <div key={index} className="border border-slate-200 rounded-xl p-6 relative">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg">Value {index + 1}</h3>
                  {values.length > 1 && (
                    <Button
                      onClick={() => removeValue(index)}
                      variant="outline"
                      size="icon"
                      className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={18} />
                    </Button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="mb-2 block">Icon Name (Lucide React)</Label>
                    <Input
                      value={value.icon}
                      onChange={(e) => updateValue(index, 'icon', e.target.value)}
                      placeholder="e.g., Target, Heart, Zap, Users"
                      data-testid={`value-icon-${index}`}
                    />
                    <p className="text-xs text-slate-500 mt-1">Use Lucide React icon names</p>
                  </div>
                  <div>
                    <Label className="mb-2 block">Title</Label>
                    <Input
                      value={value.title}
                      onChange={(e) => updateValue(index, 'title', e.target.value)}
                      placeholder="e.g., Results-Driven"
                      data-testid={`value-title-${index}`}
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Description</Label>
                  <Textarea
                    value={value.description}
                    onChange={(e) => updateValue(index, 'description', e.target.value)}
                    rows={2}
                    placeholder="Brief description of this value..."
                    data-testid={`value-description-${index}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button (Bottom) */}
        <div className="mt-8 flex justify-center">
          <Button onClick={handleSave} className="bg-[#10B981] hover:bg-[#059669] rounded-full px-8 py-6 text-lg">
            <Save size={20} className="mr-2" />
            Save All Changes
          </Button>
        </div>
      </div>
    </div>
  );
};
